import clsx from 'clsx';

import { PageWrapper } from '../../components/PageWrapper';
import { useNavigate } from 'react-router-dom';
import { HOME, SCAN } from '../../constants/routes';
import { useState, useEffect } from 'react';
import { BeatLoader } from 'react-spinners';
import { useSelector } from 'react-redux';
import useAxios from '../../axios/useAxios';
import { useJackpotMatch } from '../../hooks/useJackpotMatch';
import { formatPriceWithEuro } from '../../utils/StringUtils';
import PrimaryButton from '../../components/button/PrimaryButton';
import { useLocalStorage } from '../../hooks/useLocalStorage';

const PERCENTAGE_OPTIONS = [25, 50, 75, 100];

const Button = ({ onClick, defaultAmount, currentAmount }) => {
  return (
    <button
      type="button"
      className={clsx(
        "border border-solid border-gray-300 rounded-lg px-6 py-3 text-lg font-semibold transition-all",
        {
          "text-gray-500 bg-transparent": defaultAmount !== currentAmount,
          "scale-110 text-white bg-black": defaultAmount === currentAmount
        }
      )}
      onClick={onClick}
    >
      {defaultAmount}{' %'}
    </button>
  );
};

export const ChangeAmountJackpotScreen = () => {
  const navigate = useNavigate();
  const axiosInstance = useAxios();
  const user = useSelector((state) => state.user);
  const { store } = useSelector((state) => state.store);
  const { cartItems } = useSelector((state) => state.cart);

  const [selectedPercentage, setSelectedPercentage] = useState(100);
  const [customAmount, setCustomAmount] = useState('');
  const [inputErrorMessage, setInputErrorMessage] = useState('');

  const [, setHasChangeAmountJackpotScreen] = useLocalStorage('hasChangeAmountJackpotScreen');

  const {
    isLoadingFidCards,
    errorMessageFidCards,
    jackpotMatchSolde,
    updateJackpotAmountByValue,
  } = useJackpotMatch({
    axiosInstance,
    user,
    cartItems,
    store,
    shouldFetch: false,
  });

  useEffect(() => {
    if (jackpotMatchSolde !== null && jackpotMatchSolde !== undefined && cartItems) {
      const hasPromo = cartItems.promoPrice !== cartItems.itemsPrice;
      const finalPriceToPay = hasPromo ? cartItems.promoPrice : cartItems.itemsPrice;
      const maximumAmount = Math.min(jackpotMatchSolde || 0, finalPriceToPay || 0);
      const roundedAmount = Math.round(maximumAmount * 100) / 100;
      setCustomAmount(roundedAmount.toString());
    }
  }, [jackpotMatchSolde, cartItems]);

  const handleUpdateAmount = (percentage) => {
    setSelectedPercentage(percentage);

    const calculatedAmount = (maximumUsableAmount * percentage) / 100;
    const roundedAmount = Math.round(calculatedAmount * 100) / 100;
    setCustomAmount(roundedAmount.toString());

    if (roundedAmount > jackpotMatchSolde) {
      setInputErrorMessage(`Le montant ne peut pas dépasser ${formatPriceWithEuro(jackpotMatchSolde)}`);
      return;
    }

    const remainingAmountToPay = finalPriceToPay - roundedAmount;
    const MINIMUM_REMAINING_AMOUNT = 1;

    if (remainingAmountToPay > 0 && remainingAmountToPay < MINIMUM_REMAINING_AMOUNT) {
      setInputErrorMessage(`Le reste à payer doit être d'au moins ${formatPriceWithEuro(MINIMUM_REMAINING_AMOUNT)}`);
      return;
    }

    setInputErrorMessage('');
  };

  useEffect(() => {
    if (cartItems === null || cartItems === undefined) {
      console.info('cartItems is null or undefined, navigating to home');
      navigate(HOME);
    }
  }, [cartItems, navigate]);

  const hasPromo = cartItems?.promoPrice !== cartItems?.itemsPrice;
  const finalPriceToPay = hasPromo ? cartItems?.promoPrice : cartItems?.itemsPrice;
  const maximumUsableAmount = Math.min(jackpotMatchSolde || 0, finalPriceToPay || 0);

  const handleCustomAmountChange = (event) => {
    let value = event.target.value;

    if (value.includes('.')) {
      const parts = value.split('.');
      if (parts[1]?.length > 2) {
        value = `${parts[0]}.${parts[1].slice(0, 2)}`;
      }
    }

    const numericValue = Number.parseFloat(value);

    setCustomAmount(value);
    setSelectedPercentage(null);

    if (value === '' || value === undefined) {
      setInputErrorMessage('');
      return;
    }

    if (numericValue < 0) {
      setInputErrorMessage('Le montant ne peut pas être négatif');
      return;
    }

    if (numericValue > jackpotMatchSolde) {
      setInputErrorMessage(`Le montant ne peut pas dépasser ${formatPriceWithEuro(jackpotMatchSolde)}`);
      return;
    }

    const remainingAmountToPay = finalPriceToPay - numericValue;
    const MINIMUM_REMAINING_AMOUNT = 1;

    if (remainingAmountToPay > 0 && remainingAmountToPay < MINIMUM_REMAINING_AMOUNT) {
      setInputErrorMessage(`Le reste à payer doit être d'au moins ${formatPriceWithEuro(MINIMUM_REMAINING_AMOUNT)}`);
      return;
    }

    setInputErrorMessage('');
  };

  const handleConfirm = async () => {
    const amountValue = Number.parseFloat(customAmount) || 0;

    if (amountValue < 0 || amountValue > jackpotMatchSolde) {
      return;
    }

    const remainingAmountToPay = finalPriceToPay - amountValue;
    const MINIMUM_REMAINING_AMOUNT = 1;

    if (remainingAmountToPay > 0 && remainingAmountToPay < MINIMUM_REMAINING_AMOUNT) {
      return;
    }

    await updateJackpotAmountByValue(amountValue);

    setHasChangeAmountJackpotScreen('true');
    navigate(SCAN);
  };

  const fidCard = user?.fidCard?.[0]?.fidelityInfo?.ficheClient;
  const nom = fidCard?.nom || '';
  const prenom = fidCard?.prenom || '';
  const dateFidCard = fidCard?.dateInscription || '';

  const hasNoLoadingError = !isLoadingFidCards;
  const hasNoErrorMessage = !errorMessageFidCards;
  const shouldShowContent = hasNoLoadingError && hasNoErrorMessage;

  return (
    <PageWrapper title="Cagnotte" onGoBack={() => navigate(SCAN)}>
      <div className="flex flex-col items-center justify-center gap-6 p-6">

        {isLoadingFidCards && (
          <div className="flex flex-col items-center gap-4">
            <BeatLoader color="#183153" size={20} />
            <p className="text-gray-500 text-lg">Chargement...</p>
          </div>
        )}

        {errorMessageFidCards && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 w-full">
            <p className="text-red-600 text-lg font-semibold text-center">{errorMessageFidCards}</p>
          </div>
        )}

        {shouldShowContent ? (
          <>
            <div className="bg-gradient-to-br from-green-600 to-green-800 w-full rounded-xl p-6 shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <div className="w-16 h-16 bg-white rounded-lg p-2 flex items-center justify-center">
                  {store?.logo && <img src={store.logo} alt="logo" className="w-full h-full object-contain" />}
                </div>

                <div className="flex flex-col items-end">
                  <div className="text-white text-lg font-bold">{`${nom} ${prenom}`}</div>
                  {dateFidCard && <div className="text-white text-sm opacity-80">{dateFidCard}</div>}
                </div>
              </div>

              <div className="h-px bg-white opacity-30 my-4" />

              <div className="flex justify-between items-center">
                <div className="text-white text-base opacity-90">Solde actuel:</div>
                <div className="text-white text-3xl font-bold">
                  {formatPriceWithEuro(jackpotMatchSolde || 0)}
                </div>
              </div>
            </div>

            <div className="w-full">
              <p className="text-gray-700 text-lg font-semibold mb-4">
                Choisissez le pourcentage à utiliser:
              </p>
              <div className="flex gap-4 justify-between">
                {PERCENTAGE_OPTIONS.map((percentage) => (
                  <Button
                    key={percentage}
                    defaultAmount={percentage}
                    currentAmount={selectedPercentage}
                    onClick={() => handleUpdateAmount(percentage)}
                  />
                ))}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 w-full">
              <label htmlFor="custom-amount" className="text-gray-700 text-base font-semibold block mb-2">
                Montant à utiliser:
              </label>
              <div className="relative">
                <input
                  id="custom-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  max={jackpotMatchSolde}
                  value={customAmount}
                  onChange={handleCustomAmountChange}
                  className="w-full px-4 py-3 text-2xl font-bold text-blue-600 bg-white border-2 border-blue-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="0.00"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-600 text-xl font-semibold">
                  €
                </span>
              </div>
              {inputErrorMessage && (
                <p className="text-red-600 text-sm font-semibold mt-2">
                  {inputErrorMessage}
                </p>
              )}
              <p className="text-gray-500 text-sm mt-2">
                Solde disponible: {formatPriceWithEuro(jackpotMatchSolde)} | Montant de la commande: {formatPriceWithEuro(finalPriceToPay)}
              </p>
            </div>

            <PrimaryButton
              label="Confirmer"
              className="w-full mt-4"
              onClick={handleConfirm}
              disabled={isLoadingFidCards || inputErrorMessage !== ''}
            />
          </>
        ) : null}
      </div>
    </PageWrapper>
  );
};

export default ChangeAmountJackpotScreen;